import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';

import { environment } from 'src/environments/environment';
import { AdminWalletChannelCosts, AdminWalletSummary, WalletBalance } from 'src/app/models/Wallet';
import { OWN_ERROR_TOAST } from 'src/app/shared/interceptors/http-error.interceptor';

/**
 * Situação de uma transferência de saldo entre carteiras (01/10/2026).
 *
 * A integração com a carteira é por fila: o débito na origem sai primeiro, e o crédito no
 * destino só é enfileirado depois que a carteira confirma o débito. Por isso a transferência
 * nasce `pending_debit`, e não `completed`.
 */
export type WalletTransferStatus = 'pending_debit' | 'debited' | 'completed' | 'failed';

export interface WalletTransfer {
  id: string;
  origin_user_id: string;
  destination_user_id: string;
  amount_micros: number;
  currency: string;
  status: WalletTransferStatus;
  description: string | null;
  failure_reason: string | null;
  created_at: string;
  debited_at: string | null;
  completed_at: string | null;
  failed_at: string | null;
}

export interface WalletTransferPayload {
  origin_user_id: string;
  destination_user_id: string;
  /** Decimal em R$, como string (`"150.00"`): é o que a rota espera. */
  amount: string;
  description?: string | null;
  /**
   * Uma por tentativa lógica de transferência (uuid). Repetir com a mesma chave devolve a
   * transferência já criada (200) em vez de criar outra; a mesma chave com outro payload
   * volta 422 `code: idempotency_key_reused`.
   */
  idempotency_key: string;
}

@Injectable({ providedIn: 'root' })
export class WalletTransferService {

  constructor(private http: HttpClient) {}

  /** Saldo da carteira de um cliente, visto pelo admin (o disponível da origem). */
  userBalance(userId: string): Observable<WalletBalance> {
    return this.http.get<WalletBalance>(`${environment.apiUrl}/admin/wallet/users/${userId}/balance`);
  }

  /**
   * Saldo de VÁRIOS usuários numa chamada só (cartão da lista de Usuários, task
   * 17tm466nz8b — pedido do Bruno em 17/10): sem isso a tela faria uma ida à carteira por
   * linha da página. Lista vazia não gera request (nada para perguntar).
   */
  balances(userIds: string[]): Observable<{ data: Record<string, AdminWalletSummary> }> {
    if (userIds.length === 0) {
      return of({ data: {} });
    }

    return this.http.get<{ data: Record<string, AdminWalletSummary> }>(
      `${environment.apiUrl}/admin/wallet/balances`,
      { params: { user_ids: userIds.join(',') } },
    );
  }

  /**
   * Preço atual e gasto do mês de cada canal cobrado pela carteira, para o card "Cobrado
   * pela carteira" da lista de Usuários (subtask 17tm466vyfb). Uma chamada por página,
   * separada de `balances()` para o saldo não esperar pelas leituras de preço. O próprio card
   * avisa a falha ("Custos indisponíveis agora"): sem o toast genérico por cima.
   */
  channelCosts(userIds: string[]): Observable<{ data: Record<string, AdminWalletChannelCosts> }> {
    if (userIds.length === 0) {
      return of({ data: {} });
    }

    return this.http.get<{ data: Record<string, AdminWalletChannelCosts> }>(
      `${environment.apiUrl}/admin/wallet/channel-costs`,
      { params: { user_ids: userIds.join(',') }, context: new HttpContext().set(OWN_ERROR_TOAST, true) },
    );
  }

  /**
   * Responde 201 com `{ data: transferência }`, nascendo `pending_debit` — ou 200 com a mesma
   * transferência, quando a `idempotency_key` já foi usada com o mesmo payload.
   *
   * A tela dá os próprios avisos de erro (`OWN_ERROR_TOAST`): sem resposta é desfecho
   * incerto, e não "tente novamente".
   */
  create(payload: WalletTransferPayload): Observable<{ data: WalletTransfer }> {
    return this.http.post<{ data: WalletTransfer }>(`${environment.apiUrl}/admin/wallet/transfers`, payload, {
      context: new HttpContext().set(OWN_ERROR_TOAST, true),
    });
  }
}
